import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { Button, ComposeurConversation, FilDeConversation, IconArrowLeft, cn, entreesDuComposeur, type EntreeDeMention, type MessageVue, type PortDeFil } from '@taki/ui';
import { IconeDeFiche } from './IconeDeFiche.js';
import { resumeAvecNoms } from './mentionsNommees.js';
import { portDuFilEnMots } from './suiviEnMots.js';

/**
 * UNE CONVERSATION DU SOCLE DANS LE CENTRE (canal, message direct, chatter) :
 * le fil plat et le composeur de `@taki/ui` (contrat conversations, section 4),
 * avec le FIL D'UN MESSAGE ouvert EN PLACE : une bande de retour en tete, la
 * racine et ses reponses, le composeur qui repond dans le fil.
 */
export interface ConversationSocleProps {
  /** `null` : l'objet n'a pas encore de chatter ; le composeur le cree au premier message (`avantEnvoi`). */
  conversationId: string | null;
  port: PortDeFil;
  moi: string | null;
  entrees: readonly EntreeDeMention[];
  /** Le type de la conversation (`direct`, `canal`, `chatter`) : un direct ne propose que `@Lia` (V3-5). */
  typeDeConversation?: string | undefined;
  compact: boolean;
  /** Le message a mettre en avant (une notification « citation ») : son fil s'ouvre s'il en a un. */
  messageId?: string | null;
  /** Ce que la vue montre sans message. */
  vide: string;
  /** Pourquoi on ne peut pas ecrire (G16) ; absent : on ecrit. */
  lectureSeule?: string | null;
  /** Le chatter d'un objet qui n'a pas encore de message : le cree au premier envoi. */
  avantEnvoi?: (() => Promise<string>) | undefined;
  placeholder: string;
  /** Un message vient d'etre lu ou ecrit : le centre marque la conversation lue. */
  onActivite?: () => void;
  /** Un message vient d'etre ENVOYE (pas lu) : le chatter le signale a son bouton. */
  onEnvoye?: () => void;
  /** Un texte a poser en tete du composeur (« Demander a Lia »). */
  insertion?: { texte: string; n: number } | undefined;
  /** La fiche ouverte a l'ecran (hub-d, hub-e1) : la pastille sous le composeur dit que Lia la voit si on la cite. */
  ficheVue?: string | null;
  /** La cle du brouillon quand la conversation n'existe pas encore (chatter sans message) ; absente : celle de la conversation. */
  cleDeBrouillon?: string | undefined;
  /**
   * Le SUIVI D'UNE FICHE EN MOTS (lot Q) : chaque message lu passe par elle
   * (`messageDuSuiviEnMots`), un ancien changement a clefs se lit en mots.
   * Absente : les messages tels quels.
   */
  enMots?: ((message: MessageVue) => MessageVue) | undefined;
}

export function ConversationSocle({ conversationId, port: portDOrigine, moi, entrees, typeDeConversation, compact, messageId, vide, lectureSeule = null, avantEnvoi, placeholder, onActivite, onEnvoye, insertion, ficheVue = null, cleDeBrouillon, enMots }: ConversationSocleProps): JSX.Element {
  const [fil, setFil] = useState<MessageVue | null>(null);
  // MEMOISE : un port neuf relancerait l'ecoute du fil a chaque rendu.
  const port = useMemo(() => (enMots === undefined ? portDOrigine : portDuFilEnMots(portDOrigine, enMots)), [portDOrigine, enMots]);
  // L'activite se signale a l'OUVERTURE d'une conversation et a chaque envoi, jamais a chaque rendu :
  // la lecture ecrite en base relance l'observateur, qui relancerait l'effet.
  const activite = useRef(onActivite);
  activite.current = onActivite;
  useEffect(() => { setFil(null); if (conversationId !== null) activite.current?.(); }, [conversationId]);

  return <div className="flex min-h-0 min-w-0 flex-1 flex-col">
    {fil && <div className={cn('flex h-9 shrink-0 items-center gap-2 border-b border-border-soft text-xs text-ink-500', compact ? 'px-3' : 'px-5')}>
      <Button variant="secondary" size="sm" onClick={() => setFil(null)} aria-label="Retour à la conversation"><IconArrowLeft size={13} /> Conversation</Button>
      <span className="min-w-0 truncate">Fil de <b className="font-semibold text-ink-900">{resumeAvecNoms(fil.texte ?? '', entrees).slice(0, 60)}</b></span>
    </div>}
    {conversationId === null
      ? <div className={cn('flex min-h-0 flex-1 flex-col items-center justify-center text-center text-ink-500', compact ? 'px-5 text-[0.8125rem]' : 'px-10 text-sm')}>{vide}</div>
      : <FilDeConversation key={fil?.id ?? 'conversation'} conversationId={conversationId} port={port} moi={moi} entrees={entrees} compact={compact}
        parentId={fil?.id} vide={vide} lectureSeule={lectureSeule !== null} onOuvrirFil={fil ? undefined : (message) => setFil(message)}
        messageVise={fil ? undefined : messageId ?? undefined} className="min-h-0 flex-1" />}
    <ComposeurConversation conversationId={conversationId ?? ''} port={port} entrees={entreesDuComposeur(entrees, typeDeConversation)} compact={compact} parentId={fil?.id}
      placeholder={fil ? 'Répondre dans le fil…' : placeholder} lectureSeule={lectureSeule ?? undefined} avantEnvoi={avantEnvoi} insertion={insertion} cleDeBrouillon={cleDeBrouillon}
      onEnvoye={() => { onActivite?.(); onEnvoye?.(); }} />
    {ficheVue && <p className={cn('flex min-w-0 shrink-0 items-center gap-2 pb-2 text-[0.6875rem] text-ink-500', compact ? 'px-3' : 'px-5')}>
      <span className="flex min-w-0 shrink items-center gap-1 rounded-sm bg-accent-soft px-1.5 py-0.5 font-semibold text-accent-soft-ink"><IconeDeFiche size={10} /><span className="truncate">{ficheVue}</span></span>
      <span className="shrink-0">Lia voit cette fiche si vous la citez</span>
    </p>}
  </div>;
}
